import {
	researchApiBase,
	type ComparisonSide,
	type FeedbackBody,
	type FeedbackRef,
	type MaterialBindingBody,
	type QualityComparisonBody,
	type QualitySummary,
	type RegressionCaseBody,
	type ResearchRecord,
	type ResearchScopeRef,
	type VersionManifest,
} from "../../../shared/wire/research.ts";
import {
	associationLabel,
	attributionLabel,
	caseResultLabel,
	caseTypeLabel,
	categoryLabel,
	comparisonKindLabel,
	datasetLabel,
	judgmentLabel,
	newOutputLabel,
	generationReceiptLabel,
	recordStatusLabel,
	reviewOriginLabel,
	sourceStatusLabel,
	summaryLabel,
} from "./researchLabels";

// The feedback domain as the research renderer and the research page draw it (ADR-0089): feedback with its original
// source, correction and judgment, regression cases, comparisons with their recurrences, and the quality summary,
// which is never a score.

function artifactHref(scope: ResearchScopeRef, artifactId: string): string {
	return `${researchApiBase(scope)}/artifacts/${artifactId.split("/").map(encodeURIComponent).join("/")}`;
}

function Field({ name, children }: { name: string; children: React.ReactNode }) {
	return (
		<>
			<dt>{name}</dt>
			<dd>{children}</dd>
		</>
	);
}

/** A reference with what became of its original; a file's retained snapshot stays linked whatever the original did. */
export function FeedbackRefView({ value, scope }: { value: FeedbackRef | undefined; scope: ResearchScopeRef }) {
	if (!value) return null;
	switch (value.kind) {
		case "file":
			return (
				<span className="research-ref">
					<code>{value.path}</code>
					{value.locator && ` · ${value.locator}`}
					{value.sourceStatus !== "current" && <span className="research-state missing"> {sourceStatusLabel(value.sourceStatus)}</span>}
					{value.movedTo && ` → ${value.movedTo}`}
					{value.artifactId && (
						<>
							{" "}
							<a href={artifactHref(scope, value.artifactId)} target="_blank" rel="noreferrer">
								快照
							</a>
						</>
					)}
				</span>
			);
		case "url":
			return (
				<span className="research-ref">
					<a href={value.url} target="_blank" rel="noreferrer">
						{value.title ?? value.url}
					</a>{" "}
					<span className="hint">{sourceStatusLabel(value.sourceStatus)}</span>
				</span>
			);
		case "citekey":
			return (
				<span className="research-ref">
					<code>@{value.key}</code> <span className="hint">{sourceStatusLabel(value.sourceStatus)}</span>
				</span>
			);
		case "handle":
			return (
				<span className="research-ref">
					<code>
						{value.tool}:{value.handle}
					</code>{" "}
					<span className="research-state missing">句柄已失效</span>
				</span>
			);
		case "record":
			return (
				<span className="research-ref">
					记录 <code>{value.id}</code>
					{value.revision ? ` r${value.revision}` : ""}
				</span>
			);
		case "session":
			return (
				<span className="research-ref">
					对话 <code>{value.id}</code>
				</span>
			);
	}
}

function Refs({ values, scope }: { values: FeedbackRef[]; scope: ResearchScopeRef }) {
	return (
		<span className="research-links">
			{values.map((v, i) => (
				<FeedbackRefView key={i} value={v} scope={scope} />
			))}
		</span>
	);
}

/** The fields of a feedback-domain record a person reads it by. */
export function FeedbackFields({ record, scope }: { record: ResearchRecord; scope: ResearchScopeRef }) {
	switch (record.kind) {
		case "feedback": {
			const b = record.body as unknown as FeedbackBody;
			return (
				<dl className="research-fields">
					<Field name="类别">{categoryLabel(b.category)}</Field>
					<Field name="原始来源">
						<FeedbackRefView value={b.sourceRef} scope={scope} />
					</Field>
					<Field name="描述">{b.description}</Field>
					<Field name="更正">{b.correction}</Field>
					<Field name="理由">{b.reason}</Field>
					<Field name="审稿来源">{reviewOriginLabel(b.reviewOrigin)}</Field>
					<Field name="判定">{judgmentLabel(b.judgment)}</Field>
					<Field name="数据来源">{datasetLabel(b.datasetOrigin)}</Field>
					{b.verification && (
						<Field name="核实">
							{[b.verification.method, b.verification.actor].filter(Boolean).join(" · ")} <FeedbackRefView value={b.verification.source} scope={scope} />
						</Field>
					)}
					{b.verificationRefs.length > 0 && (
						<Field name="核实依据">
							<Refs values={b.verificationRefs} scope={scope} />
						</Field>
					)}
				</dl>
			);
		}
		case "regression_case": {
			const b = record.body as unknown as RegressionCaseBody;
			const v = b.verification;
			return (
				<dl className="research-fields">
					<Field name="用例类型">{caseTypeLabel(b.caseType)}</Field>
					<Field name="新输出">{newOutputLabel(b.newOutputStatus)}</Field>
					<Field name="检查">
						<code>{b.grader.type}</code> <code>{b.grader.type === "regex_match" ? b.grader.pattern : b.grader.value}</code>
					</Field>
					<Field name="类别">
						{categoryLabel(b.category)} · {reviewOriginLabel(b.reviewOrigin)} · {judgmentLabel(b.judgment)} · {datasetLabel(b.datasetOrigin)}
					</Field>
					<Field name="来源反馈">
						<code>{b.feedbackId}</code> r{b.feedbackRevision}
					</Field>
					<Field name="核实">
						{v.kind === "objective" ? `${v.method} · ` : ""}
						{v.actor} <FeedbackRefView value={v.kind === "objective" ? v.artifactRef : v.source} scope={scope} />
					</Field>
					<Field name="断言追溯">{b.oracleTraced === null ? "未知" : b.oracleTraced ? "已追溯" : "未追溯"}</Field>
					{b.fixtureHash && (
						<Field name="样例哈希">
							<code>{b.fixtureHash}</code>
						</Field>
					)}
					<Field name="更正">{b.correction}</Field>
					<Field name="理由">{b.reason}</Field>
					{b.correctionHistory.length > 0 && (
						<Field name="更正历史">
							<ol className="research-list">
								{b.correctionHistory.map((h) => (
									<li key={h.revision}>
										r{h.revision} · {recordStatusLabel(h.status)} · {h.correction}
									</li>
								))}
							</ol>
						</Field>
					)}
					<Field name="来源">
						<Refs values={b.sourceRefs} scope={scope} />
					</Field>
				</dl>
			);
		}
		case "material_binding": {
			const b = record.body as unknown as MaterialBindingBody;
			return (
				<dl className="research-fields">
					{b.title && <Field name="标题">{b.title}</Field>}
					<Field name="材料">
						<FeedbackRefView value={b.materialRef} scope={scope} />
					</Field>
					<Field name="关联">{associationLabel(record.status === "confirmed" ? "confirmed" : b.associationStatus)}</Field>
					<Field name="目标">{b.targetScope.kind === "project" ? `项目 ${b.targetScope.projectId}` : "收件箱"}</Field>
					<Field name="依据">{b.rationale}</Field>
				</dl>
			);
		}
		case "quality_comparison": {
			const b = record.body as unknown as QualityComparisonBody;
			return (
				<>
					<dl className="research-fields">
						<Field name="对比">{b.status === "incomparable" ? "不可比" : "已对比"}</Field>
						<Field name="对比类型">{comparisonKindLabel(b.comparisonKind)}</Field>
						<Field name="归因">{attributionLabel(b.attribution)}</Field>
						<Side name="基线" side={b.baseline} scope={scope} />
						<Side name="候选" side={b.candidate} scope={scope} />
						<Field name="覆盖">覆盖 {b.coverage.total} 个用例</Field>
						<Field name="整体质量">未评估</Field>
						{b.recurrences.length > 0 && <Field name="复发用例">{b.recurrences.join("、")}</Field>}
						{b.preferenceMisses.length > 0 && <Field name="偏好未满足">{b.preferenceMisses.join("、")}</Field>}
						{b.notRun.length > 0 && (
							<Field name="未运行">
								<ul className="research-list">
									{b.notRun.map((n) => (
										<li key={n.caseId}>
											<code>{n.caseId}</code> · {n.reason}
										</li>
									))}
								</ul>
							</Field>
						)}
						{b.reasons.length > 0 && <Field name="说明">{b.reasons.join("；")}</Field>}
					</dl>
					<ComparisonTables body={b} />
				</>
			);
		}
		default:
			return null;
	}
}

function Side({ name, side, scope }: { name: string; side: ComparisonSide; scope: ResearchScopeRef }) {
	return (
		<Field name={name}>
			<FeedbackRefView value={side.artifactRef} scope={scope} /> · {generationReceiptLabel(side.receiptStatus)}
			{side.receipt && (
				<>
					{" "}
					· 对话 <code>{side.receipt.sessionId}</code> · 调用 <code>{side.receipt.toolCallId}</code>
					{side.receipt.manifest.model ? ` · 模型 ${side.receipt.manifest.model}` : ""}
				</>
			)}
			{side.receiptReason && ` · ${side.receiptReason}`}
		</Field>
	);
}

const HASHES: Array<keyof VersionManifest> = ["fixtureHash", "graderHash", "profileHash", "systemHash", "skillHash", "processHash", "packageHash", "model", "promptHash", "executionProvenance"];

function ComparisonTables({ body }: { body: QualityComparisonBody }) {
	const hashes = HASHES.filter((h) => body.hashes.baseline[h] !== undefined || body.hashes.candidate[h] !== undefined);
	return (
		<>
			{body.cases.length > 0 && (
				<table className="research-table">
					<thead>
						<tr>
							<th>用例</th>
							<th>类型</th>
							<th>基线</th>
							<th>候选</th>
							<th>复发</th>
						</tr>
					</thead>
					<tbody>
						{body.cases.map((c) => (
							<tr key={c.caseId} className={c.recurred ? "recurred" : undefined}>
								<td>
									<code>{c.caseId}</code> r{c.caseRevision}
								</td>
								<td>{caseTypeLabel(c.caseType)}</td>
								<td>{caseResultLabel(c.baseline)}</td>
								<td>{caseResultLabel(c.candidate)}</td>
								<td>{c.recurred ? "复发" : ""}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
			{hashes.length > 0 && (
				<table className="research-table">
					<thead>
						<tr>
							<th>哈希</th>
							<th>基线</th>
							<th>候选</th>
						</tr>
					</thead>
					<tbody>
						{hashes.map((h) => (
							<tr key={h} className={body.hashes.differing.includes(h) ? "differing" : undefined}>
								<td>{h}</td>
								<td>
									<code>{body.hashes.baseline[h] ?? "—"}</code>
								</td>
								<td>
									<code>{body.hashes.candidate[h] ?? "—"}</code>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</>
	);
}

/** What the stored cases and comparisons support saying about quality: a state with its reasons, never a score. */
export function QualitySummaryCard({ summary }: { summary: QualitySummary }) {
	const c = summary.cases;
	return (
		<div className="research-block">
			<p className="research-meta">
				<span className={`research-state ${summary.status === "bounded" ? "" : "stale"}`}>{summaryLabel(summary.status)}</span> · 整体质量 未评估
			</p>
			{summary.reasons.length > 0 && <p className="hint">{summary.reasons.join("；")}</p>}
			<dl className="research-fields">
				<Field name="用例">
					{c.total}（客观 {c.objective} · 人工裁定 {c.humanJudgment} · 偏好 {c.preference} · 真实已核实 {c.verifiedReal} · 合成 {c.synthetic}）
				</Field>
				<Field name="反馈">{summary.feedback.total}</Field>
				<Field name="对比">
					{summary.comparisons.total}（不可比 {summary.comparisons.incomparable} · 有界 {summary.comparisons.bounded}）
				</Field>
				{summary.comparisons.recurrences.length > 0 && <Field name="复发用例">{summary.comparisons.recurrences.join("、")}</Field>}
			</dl>
			{summary.fixtureResults.length > 0 && (
				<table className="research-table">
					<thead>
						<tr>
							<th>用例</th>
							<th>样例结果</th>
							<th>对比</th>
						</tr>
					</thead>
					<tbody>
						{summary.fixtureResults.map((r) => (
							<tr key={r.caseId}>
								<td>
									<code>{r.caseId}</code>
								</td>
								<td>{caseResultLabel(r.result)}</td>
								<td>
									<code>{r.comparisonId}</code>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

function versions(body: QualityComparisonBody): string {
	return HASHES.filter((h) => body.hashes.differing.includes(h))
		.map((h) => `${h} ${body.hashes.baseline[h] ?? "—"} → ${body.hashes.candidate[h] ?? "—"}`)
		.join("；");
}

/**
 * Each case's result in every stored comparison, newest first, with the versions that differed between the two
 * outputs: where a corrected error came back. Coverage counts the cases any comparison ran.
 */
export function RecurrenceTable({ comparisons, cases }: { comparisons: ResearchRecord[]; cases: number }) {
	if (comparisons.length === 0) return <p className="hint">暂无对比</p>;
	const bodies = comparisons.map((c) => ({ id: c.id, body: c.body as unknown as QualityComparisonBody }));
	const covered = new Set(bodies.flatMap((c) => c.body.coverage.caseIds));
	const notRun = bodies.flatMap((c) => c.body.notRun.map((n) => ({ ...n, comparisonId: c.id })));
	return (
		<>
			<table className="research-table" aria-label="复发">
				<thead>
					<tr>
						<th>用例</th>
						<th>对比</th>
						<th>版本差异</th>
						<th>基线</th>
						<th>候选</th>
						<th>复发</th>
					</tr>
				</thead>
				<tbody>
					{bodies.flatMap(({ id, body }) =>
						body.cases.map((c) => (
							<tr key={`${id}:${c.caseId}`} className={c.recurred ? "recurred" : undefined}>
								<td>
									<code>{c.caseId}</code> r{c.caseRevision} · {caseTypeLabel(c.caseType)}
								</td>
								<td>
									<code>{id}</code> · {comparisonKindLabel(body.comparisonKind)}
								</td>
								<td>{versions(body) || "—"}</td>
								<td>{caseResultLabel(c.baseline)}</td>
								<td>{caseResultLabel(c.candidate)}</td>
								<td>{c.recurred ? "复发" : ""}</td>
							</tr>
						)),
					)}
				</tbody>
			</table>
			<p className="hint">
				覆盖 {covered.size} / {cases} 个用例 · {comparisons.length} 次对比
			</p>
			{notRun.length > 0 && (
				<ul className="research-list" aria-label="未运行">
					{notRun.map((n) => (
						<li key={`${n.comparisonId}:${n.caseId}`}>
							未运行 <code>{n.caseId}</code> · {n.reason}
						</li>
					))}
				</ul>
			)}
		</>
	);
}
